/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import styles from "./reportPanel.module.css";

const cx = (...parts) => parts.filter(Boolean).join(" ");
const dash = (v) => (v === undefined || v === null || v === "" ? "-" : v);
const dateText = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};
const compactDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};
const elapsedText = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  const minutes = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};
const elapsedDays = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(1, Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000)) + 1);
};
const jsonText = (v) => {
  if (v === undefined || v === null || v === "") return "-";
  if (typeof v === "string") {
    try {
      return JSON.stringify(JSON.parse(v));
    } catch (e) {
      return v;
    }
  }
  return typeof v === "object" ? JSON.stringify(v) : String(v);
};
const REPLACEMENT_TYPE_LABELS = { maintain: "Maintain", regression: "Regression", progression: "Progression" };
const replacementTypeText = (v) => {
  if (!v) return "-";
  const key = String(v).toLowerCase();
  return REPLACEMENT_TYPE_LABELS[key] || v;
};
function RecommendationList({ recommendations, selectedActivityId }) {
  if (!Array.isArray(recommendations) || !recommendations.length) return null;
  return (
    <div className={styles.recommendationList}>
      {recommendations.map((rec, i) => {
        const isSelected = selectedActivityId != null && String(rec?.exercise_id) === String(selectedActivityId);
        return (
          <div className={styles.recommendationRow} key={i}>
            <strong>{dash(rec?.exercise_name)}{isSelected ? " (selected)" : ""}</strong>
            {rec?.rationale ? ` - ${rec.rationale}` : ""}
          </div>
        );
      })}
    </div>
  );
}

function getAuthToken() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}")?.token;
  } catch {
    return null;
  }
}

function authHeader() {
  const token = getAuthToken();
  if (token) axios.defaults.headers.common["Authorization"] = token;
}

function recordLine(t) {
  let text = dash(t.event_type);
  if (t.action_taken) text += ` - ${t.action_taken}`;
  if (/^(?:panel-report-|pt-)/.test(String(t.event_type || "")) && t.raw_payload) {
    try {
      const payload = typeof t.raw_payload === "string" ? JSON.parse(t.raw_payload) : t.raw_payload;
      const detail = payload?.reason || payload?.closingAction || payload?.decision || payload?.clinicalRationale;
      if (detail) text += ` - ${detail}`;
    } catch (e) {
      // Keep the normal timeline text if an old payload cannot be parsed.
    }
  }
  if (t.actor) text += ` (${t.actor})`;
  return text;
}

const FILTERS = [
  { key: "exc", label: "Exceptions first" },
  { key: "decision", label: "Waiting on decision" },
  { key: "all", label: "Everyone" },
  { key: "measure", label: "Measure due" },
  { key: "paused", label: "Paused" },
];

const STATES = [
  { value: "", label: "All states" },
  { value: "District of Columbia", label: "District of Columbia" },
  { value: "Delaware", label: "Delaware" },
  { value: "Maryland", label: "Maryland" },
  { value: "New Jersey", label: "New Jersey" },
  { value: "Pennsylvania", label: "Pennsylvania" },
  { value: "Virginia", label: "Virginia" },
];

const STANDARD_DECISION_TYPES = new Set(["T2", "T3", "T6b"]);
const DISCOVERY_SOURCE_TYPES = new Set(["T4a", "T4b", "T6a"]);

function Timeline({ rows, emptyText = "No records." }) {
  if (!rows?.length) return <div className={styles.note}>{emptyText}</div>;
  return (
    <div className={styles.timeline}>
      {rows.map((row, i) => (
        <div className={styles.tl} key={row.id || row.history_id || `${row.date || row.created_at}-${i}`}>
          <div className={styles.d}>{compactDate(row.date || row.created_at || row.event_at || row.recorded_at || row.collection_date)}</div>
          <div>{row.text || recordLine(row)}</div>
        </div>
      ))}
    </div>
  );
}

function Detail({
  p,
  d,
  onPainDecision,
  painDecisionSaving,
  onExceptionDecision,
  exceptionDecisionSaving,
  onPanelAction,
  panelActionSaving,
  onAuditExport,
  auditExporting,
}) {
  const [revisitDates, setRevisitDates] = useState({});
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [fullHistory, setFullHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const alignment = d?.alignment || {};
  const condition = d?.condition || {};
  const qualifying = d?.qualifyingCondition || {};
  const outcomeRows = d?.outcomeMeasures || [];
  const stateHistory = d?.stateHistory || [];
  const currentPlan = d?.currentPlan || [];
  const planRevisionHistory = (d?.planRevisionHistory || []).filter(
    (row) => row.replacement_type || row.trigger_reason || (Array.isArray(row.final_recommendations) && row.final_recommendations.length)
  );
  const painHistory = d?.painHistory || [];
  const painCrossings = d?.painCrossings || [];
  const assessmentSafety = d?.assessmentSafety || null;
  const safetyRows = d?.ongoingSafetyPrompts || [];
  const escalationRows = d?.escalationRecords || [];
  const holdPauseRows = d?.holdPauseRecords || [];
  const conversations = d?.conversations || [];
  const careTeamRows = d?.careTeam || [];
  const careTeamEvents = d?.careTeamEvents || [];
  const careUpdates = d?.careUpdates || [];
  const exceptions = d?.openExceptions || p.openExceptions || [];
  const exceptionHistory = d?.exceptionHistory || [];
  const decisionRecords = d?.decisionRecords || [];
  const timeline = d?.timeline || [];
  const displayedCareState = d?.currentState || p.currentState;
  const currentCareStateRecord = [...holdPauseRows].reverse().find((row) => row.state === displayedCareState);
  const measureScheduleWarning = ["held", "paused"].includes(displayedCareState) && !!currentCareStateRecord?.measureScheduleImpact;
  console.log("decision records : ",decisionRecords)

  const loadFullHistory = async (force = false) => {
    if (fullHistory && !force) return;
    authHeader();
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${p.beneficiaryId}/history`);
      if (resp.data.success) setFullHistory(resp.data.data || []);
      else setHistoryError(resp.data.message || "Failed to load full history.");
    } catch (err) {
      setHistoryError(err.response?.data?.message || "Failed to load full history.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleFullHistory = async () => {
    if (showFullHistory) {
      setShowFullHistory(false);
      return;
    }
    setShowFullHistory(true);
    await loadFullHistory();
  };

  const ask = (label, required = false) => {
    const entered = window.prompt(label, "");
    if (entered === null) return null;
    const clean = entered.trim();
    if (required && !clean) {
      window.alert("This field is required for the record.");
      return null;
    }
    return clean;
  };

  const requestPanelAction = async (exception, action) => {
    if (action === "message_beneficiary") {
      const message = ask("Message to the beneficiary:", true);
      if (!message) return;
      const saved = await onPanelAction(action, message, { exceptionId: exception?.exceptionId || null });
      if (saved && showFullHistory) await loadFullHistory(true);
      return;
    }
    if (action === "record_exclusion_discovery") {
      const reason = ask("Clinical needs identified on exclusion discovery:", true);
      if (!reason) return;
      const categoryDefault = exception?.sourceSnapshot?.exclusion_category || exception?.sourceSnapshot?.exclusionCategory || "";
      const categoryEntered = window.prompt("Excluded condition category (1-7):", String(categoryDefault || ""));
      if (categoryEntered === null) return;
      const category = Number(categoryEntered);
      if (!Number.isInteger(category) || category < 1 || category > 7) {
        window.alert("Enter an excluded condition category from 1 through 7.");
        return;
      }
      const saved = await onPanelAction(action, reason, {
        exceptionId: exception?.exceptionId || null,
        exclusionCategory: category,
      });
      if (saved && showFullHistory) await loadFullHistory(true);
      return;
    }
    const reason = ask(action === "add_note" ? "Note to add to the record:" : "Reason for acknowledging and closing this notification:", true);
    if (!reason) return;
    const saved = await onPanelAction(action, reason, { exceptionId: exception?.exceptionId || null });
    if (saved && showFullHistory) await loadFullHistory(true);
  };

  const requestStandardDecision = async (exception, decision) => {
    const reviewDate = revisitDates[exception.exceptionId] || "";
    if ((decision === "keep_holding" || decision === "resume_changed_plan") && !reviewDate) {
      window.alert(decision === "keep_holding"
        ? "A review date is required when care remains held."
        : "A date is required to confirm when the modified plan takes effect before resuming.");
      return;
    }
    let clinicalNeedsIdentified = null;
    let clinicalDataRequested = null;
    let beneficiaryWording = null;
    if (decision === "be_seen_in_person") {
      clinicalNeedsIdentified = ask("Clinical needs identified for the escalation record:", true);
      if (!clinicalNeedsIdentified) return;
      clinicalDataRequested = ask("Clinical data requested or sent. Enter 'no' or describe what was sent, to whom, and when:", true);
      if (!clinicalDataRequested) return;
      beneficiaryWording = ask("Version or wording shown to the beneficiary:", true);
      if (!beneficiaryWording) return;
    }
    const payload = {
      revisitDate: (decision === "keep_holding" || decision === "resume_changed_plan") ? reviewDate : null,
      clinicalNeedsIdentified,
      clinicalDataRequested,
      beneficiaryWording,
    };
    if (["T2", "T3"].includes(exception.exceptionType)) await onPainDecision(exception, decision, payload);
    else await onExceptionDecision(exception, decision, payload);
  };

  const requestT10Decision = async (exception, decision) => {
    const clinicalRationale = ask("Documented clinical rationale:", true);
    if (!clinicalRationale) return;
    const clinicalDataRequested = ask("Clinical data requested or sent. Enter 'no' or describe what was sent, to whom, and when:", true);
    if (!clinicalDataRequested) return;
    const beneficiaryWording = ask("Version or wording shown to the beneficiary:", true);
    if (!beneficiaryWording) return;
    await onExceptionDecision(exception, decision, {
      clinicalRationale,
      clinicalDataRequested,
      beneficiaryWording,
    });
  };

  const planGroups = [];
  currentPlan.forEach((row) => {
    let group = planGroups.find((item) => item.taskId === row.task_id);
    if (!group) {
      group = { taskId: row.task_id, name: row.task_name, date: row.suggested_date, exercises: [] };
      planGroups.push(group);
    }
    if (row.activity_id) group.exercises.push(row);
  });

  return (
    <>
      {exceptions.map((exception) => (
        <div
          className={cx(styles.excbar, (exception.kind === "decision" || exception.exceptionType === "T4b") && styles.excbarStop)}
          key={exception.exceptionId}
        >
          <b>
            {exception.exceptionType}: {dash(exception.label)} - {exception.kind === "decision" ? "Decision" : "Notification"}
            {exception.state === "escalated" ? " - ESCALATED" : ""}
          </b>
          Raised: {dateText(exception.raisedAt)}. Elapsed: {elapsedText(exception.raisedAt)}. 48-hour elevation: {dateText(exception.escalationDueAt)}.
          {exception.kind === "decision" && <> 24-hour response target: {dateText(exception.decisionDueAt)}.</>}
          {exception.reviewDate && <> Review date: {compactDate(exception.reviewDate)}.</>}
          {(exception.sourceSnapshot?.intent === "access_nursing_home_admission" || exception.sourceSnapshot?.nursingHomeAdmissionDate) && (
            <>
              <br />Nursing-home day counter: {elapsedDays(exception.sourceSnapshot?.nursingHomeAdmissionDate || exception.sourceSnapshot?.admission_date) ?? "-"} days.
              Day 101 flag date: {compactDate(exception.sourceSnapshot?.nursingHomeDay101At || (exception.sourceSnapshot?.admission_date ? new Date(new Date(exception.sourceSnapshot.admission_date).getTime() + 100 * 24 * 60 * 60 * 1000) : null))}.
            </>
          )}
          {["T6a", "T6b"].includes(exception.exceptionType) && exception.sourceSnapshot?.answers && (
            <><br />Return conversation: {jsonText(exception.sourceSnapshot.answers)}</>
          )}
          {exception.beneficiaryNote && <><br />Beneficiary note: {exception.beneficiaryNote}</>}
        </div>
      ))}

      {!exceptions.length && <div className={styles.good}>No open therapist exceptions for this beneficiary.</div>}

      <div className={styles.dgrid}>
        <div className={styles.dcard}>
          <h5>Beneficiary and alignment</h5>
          <div className={styles.dl}>
            <div>Name</div><div>{`${p.legalFirstName || ""} ${p.legalLastName || ""}`.trim() || "-"}</div>
            <div>Internal identifier</div><div>{dash(p.beneficiaryId)}</div>
            <div>Licensure state</div><div>{dash(assessmentSafety?.state)}</div>
            <div>Register with state</div><div>{dash(p.state)}</div>
            <div>Alignment date</div><div>{dateText(alignment.alignment_date)}</div>
            <div>Care initiation date</div><div>{dateText(d?.careInitiationDate)}</div>
            <div>Alignment status</div><div>{dash(alignment.alignment_state)}</div>
            <div>Current state</div><div>{dash(d?.currentState || p.currentState)}</div>
            <div>Care furnished this month</div><div>{d?.careFurnishedThisMonth ? "Yes" : "No"}</div>
          </div>
        </div>

        <div className={styles.dcard}>
          <h5>Condition and classification</h5>
          <div className={styles.dl}>
            <div>Qualifying condition</div><div>{dash(qualifying.condition_display)}</div>
            <div>Diagnosis code</div><div>{dash(qualifying.condition_code)}</div>
            <div>Code system</div><div>{dash(qualifying.condition_system)}</div>
            <div>Guideline</div><div>{dash(condition.cpg_label ?? condition.final_cpg_classification)}</div>
            <div>Classification</div><div>{dash(condition.cpg_value ?? condition.cpg_id ??condition.final_cpg_classification )}</div>
            <div>Pain subtype</div><div>{dash(condition.final_pain_subtype)}</div>
          </div>
        </div>

        <div className={styles.dcard}>
          <h5>State history</h5>
          <Timeline
            rows={stateHistory.map((row, i) => ({ ...row, id: `state-${i}`, created_at: row.date, event_type: row.state, action_taken: row.reason }))}
            emptyText="No state history on record."
          />
        </div>

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Current plan</h5>
          {planGroups.length ? planGroups.map((task) => (
            <div className={styles.timeline} key={task.taskId}>
              <div className={styles.tl}>
                <div className={styles.d}>{compactDate(task.date)}</div>
                <div>
                  <b>{dash(task.name)}</b>
                  {task.exercises.length ? task.exercises.map((exercise) => (
                    <div key={`${task.taskId}-${exercise.activity_id}`}>
                      {dash(exercise.display_name)}; reps {dash(exercise.reps)}; hold {dash(exercise.hold_in_seconds)} sec; duration {dash(exercise.duration)}
                    </div>
                  )) : <div>No exercise rows on this task.</div>}
                </div>
              </div>
            </div>
          )) : <div className={styles.note}>No active plan rows on record.</div>}
        </div>

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Plan revision history</h5>
          <Timeline
            rows={planRevisionHistory.map((row) => ({
              ...row,
              created_at: row.recorded_at || row.created_at,
              text: (
                <div>
                  <div>
                    {replacementTypeText(row.replacement_type)}
                    {row.trigger_reason ? ` - ${row.trigger_reason}` : ""}
                  </div>
                  <RecommendationList recommendations={row.final_recommendations} selectedActivityId={row.new_activity_id} />
                </div>
              ),
            }))}
            emptyText="No progression, regression, or beneficiary change is recorded."
          />
        </div>

        <div className={styles.dcard}>
          <h5>Pain history</h5>
          <Timeline
            rows={painHistory.map((row) => ({ id: `pain-${row.id}`, created_at: row.date, event_type: `Pain ${row.pain} of 10` }))}
            emptyText="No pain scores on record."
          />
        </div>

        <div className={styles.dcard}>
          <h5>Pain rule crossings</h5>
          <Timeline
            rows={painCrossings.map((row) => ({
              ...row,
              created_at: row.created_at || row.date,
              event_type: `${dash(row.crossing_seq)} crossing`,
              action_taken: `${dash(row.window_type)}; ${dash(row.reference_pain)} to ${dash(row.new_pain)}; status ${dash(row.status)}`,
            }))}
            emptyText="No pain rule crossings on record."
          />
        </div>

        <div className={styles.dcard}>
          <h5>Outcome measures</h5>
          <div className={styles.dl}>
            <div>Next window opens</div><div>{dateText(alignment.next_reporting_window_open || p.next_reporting_window_open)}</div>
            <div>Next window closes</div><div>{dateText(alignment.next_reporting_window_close || p.next_reporting_window_close)}</div>
            <div>Pain now</div><div>{p.pain_level != null ? `${p.pain_level} of 10` : "-"}</div>
          </div>
          {outcomeRows.length ? outcomeRows.map((o, i) => (
            <div className={styles.dl} key={`${o.instrument_key || o.instrument_label}-${i}`} style={{ marginTop: ".65rem", paddingTop: ".45rem", borderTop: "1px solid var(--gray200)" }}>
              <div>Instrument</div><div>{dash(o.instrument_label || o.instrument_key)}</div>
              <div>Baseline</div><div>{dash(o.baseline_value)}</div>
              <div>Most recent</div><div>{dash(o.latest_value)}</div>
              <div>Collected</div><div>{dateText(o.latest_collection_date)}</div>
              <div>Target</div><div>{dash(o.target_value)}</div>
              <div>Scale</div><div>{o.scale_min != null && o.scale_max != null ? `${o.scale_min}-${o.scale_max}` : "-"}</div>
            </div>
          )) : <div className={styles.note}>No outcome instruments on record.</div>}
        </div>

        <div className={styles.dcard}>
          <h5>Assessment and ongoing safety record</h5>
          <div className={styles.dl}>
            <div>Pre-existing conditions</div><div>{jsonText(condition.pre_existing_conditions)}</div>
            <div>Current conditions</div><div>{jsonText(condition.current_conditions)}</div>
            <div>Assessment coverage answers</div><div>{jsonText(assessmentSafety?.coverageAnswers)}</div>
            <div>Exclusion items shown</div><div>{jsonText(assessmentSafety?.exclusionItemsShown)}</div>
            <div>Exclusion items fired</div><div>{jsonText(assessmentSafety?.exclusionItemsFired)}</div>
            <div>Assessment outcome</div><div>{dash(assessmentSafety?.outcome)}</div>
            <div>Assessment wording version</div><div>{dash(assessmentSafety?.wordingVersion)}</div>
          </div>
          <div style={{ marginTop: ".8rem" }}>
            <Timeline
              rows={safetyRows.map((row) => ({ ...row, action_taken: `${dash(row.action_taken)} ${jsonText(row.raw_payload)}`.trim() }))}
              emptyText="No ongoing safety prompt flags or hard stops on record."
            />
          </div>
        </div>

        <div className={styles.dcard}>
          <h5>Hold and pause records</h5>
          {measureScheduleWarning && (
            <div className={styles.note}>The next measurement window may close while care is {displayedCareState}.</div>
          )}
          <Timeline
            rows={holdPauseRows.map((row) => ({
              ...row,
              event_type: dash(row.state),
              action_taken: `${dash(row.reason)}; rationale ${dash(row.clinicalRationale)}; billing ${dash(row.billingPosition)}`,
            }))}
            emptyText="No hold or pause state records on file."
          />
        </div>

        <div className={styles.dcard}>
          <h5>Decision records</h5>
          <Timeline
            rows={decisionRecords.map((row) => ({
              ...row,
              event_type: `${dash(row.exceptionType)} - ${dash(row.decision)}`,
              action_taken: `PT ${dash(row.decidedByName || row.actor)}; 24h met ${row.responseTimeMet === null || row.responseTimeMet === undefined ? "-" : row.responseTimeMet ? "yes" : "no"}`,
            }))}
            emptyText="No therapist decisions on file."
          />
        </div>

        <div className={styles.dcard}>
          <h5>Escalation records</h5>
          <Timeline
            rows={escalationRows.map((row) => ({
              ...row,
              event_type: `${dash(row.source)} - ${dash(row.directionGiven)}`,
              action_taken: `${dash(row.clinicalNeedsIdentified)}; plan ${dash(row.planState)}; clinician ${dash(row.clinicianNotified)}`,
            }))}
            emptyText="No escalation records on file."
          />
        </div>

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Exception history (including closed and acknowledged)</h5>
          <Timeline
            rows={exceptionHistory.map((row, i) => ({
              ...row,
              id: row.exceptionId || row.id || i,
              created_at: row.raisedAt || row.created_at,
              event_type: `${dash(row.exceptionType)}: ${dash(row.label)} - ${row.state === "closed" ? "Closed" : row.state === "escalated" ? "Escalated" : "Open"}`,
              action_taken: row.state === "closed"
                ? `Closed by ${dash(row.closedBy)} (${dash(row.closingAction)}) at ${dateText(row.closedAt)}`
                : dash(row.action_taken),
            }))}
            emptyText="No pain-rule or safety exceptions on record for this beneficiary."
          />
        </div>

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Myra conversation</h5>
          <Timeline
            rows={conversations.map((row, i) => ({
              ...row,
              id: `${row.conversation_id}-${row.turn_number}-${i}`,
              event_type: `${dash(row.role)}${row.touchpoint ? ` (${row.touchpoint})` : ""}`,
              action_taken: row.content,
            }))}
            emptyText="No Myra conversation rows on record."
          />
        </div>

        <div className={styles.dcard}>
          <h5>Care team</h5>
          {careTeamRows.length ? careTeamRows.map((row, i) => (
            <div className={styles.dl} key={row.id || i} style={{ marginBottom: ".65rem" }}>
              <div>Clinician</div><div>{dash(row.clinician_name)}</div>
              <div>NPI</div><div>{dash(row.npi)}</div>
              <div>Status</div><div>{dash(row.status)}</div>
              <div>Direct</div><div>{row.has_direct ? dash(row.direct_endpoint) : "No"}</div>
            </div>
          )) : <div className={styles.note}>No care team row on record.</div>}
        </div>

        <div className={styles.dcard}>
          <h5>Care Updates</h5>
          <Timeline
            rows={careUpdates.map((row) => ({
              ...row,
              event_type: row.update_type || row.artifact_type || row.status || "Care Update",
              action_taken: row.delivery_outcome || row.result_text || row.destination || jsonText(row),
            }))}
            emptyText="No Care Update artifacts on record."
          />
        </div>

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Care team events and delivery outcomes</h5>
          <Timeline
            rows={careTeamEvents.map((row) => ({ ...row, created_at: row.event_at, event_type: row.event, action_taken: jsonText(row.detail) }))}
            emptyText="No care team delivery events on record."
          />
        </div>


        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Therapist actions</h5>
          {exceptions.length ? exceptions.map((exception) => {
            const saving = painDecisionSaving || exceptionDecisionSaving || panelActionSaving;
            return (
              <div key={`actions-${exception.exceptionId}`} style={{ borderTop: "1px solid var(--gray200)", paddingTop: ".8rem", marginTop: ".8rem" }}>
                <div><b>{exception.exceptionType}: {exception.label}</b> ({exception.kind})</div>
                {exception.kind === "notification" && (
                  <div className={styles.acts}>
                    <button className={styles.btn} disabled={saving} onClick={() => requestPanelAction(exception, "add_note")}>Add a note to the record</button>
                    <button className={styles.btn} disabled={saving} onClick={() => requestPanelAction(exception, "message_beneficiary")}>Message the beneficiary</button>
                    {DISCOVERY_SOURCE_TYPES.has(exception.exceptionType) && (
                      <button className={styles.btn} disabled={saving} onClick={() => requestPanelAction(exception, "record_exclusion_discovery")}>Record exclusion discovery</button>
                    )}
                    <button className={styles.btnPrimary + " " + styles.btn} disabled={saving} onClick={() => requestPanelAction(exception, "acknowledge_close")}>Acknowledge and close</button>
                  </div>
                )}

                {exception.kind === "decision" && STANDARD_DECISION_TYPES.has(exception.exceptionType) && (
                  <>
                    <div className={styles.acts}>
                      <button className={styles.btnPrimary + " " + styles.btn} disabled={saving} onClick={() => requestStandardDecision(exception, "resume_changed_plan")}>Resume with a modified plan</button>
                      <button className={styles.btn} disabled={saving} onClick={() => requestStandardDecision(exception, "keep_holding")}>Hold, with a date to look again</button>
                      <button className={styles.btnDanger + " " + styles.btn} disabled={saving} onClick={() => requestStandardDecision(exception, "be_seen_in_person")}>Refer out for an in-person assessment</button>
                    </div>
                    <div className={styles.filters} style={{ marginTop: ".7rem", marginBottom: 0 }}>
                      <label htmlFor={`review-${exception.exceptionId}`}>Review date for hold</label>
                      <input
                        id={`review-${exception.exceptionId}`}
                        type="date"
                        value={revisitDates[exception.exceptionId] || ""}
                        onChange={(e) => setRevisitDates((prev) => ({ ...prev, [exception.exceptionId]: e.target.value }))}
                      />
                    </div>
                    <div className={styles.acts}>
                      <button className={styles.btn} disabled={saving} onClick={() => requestPanelAction(exception, "add_note")}>Add a note to the record</button>
                      <button className={styles.btn} disabled={saving} onClick={() => requestPanelAction(exception, "message_beneficiary")}>Message the beneficiary</button>
                    </div>
                  </>
                )}

                {exception.kind === "decision" && exception.exceptionType === "T10" && (
                  <>
                    <div className={styles.note}>
                      Removal from alignment can be recorded, but the CMS Unalignment API submission is intentionally blocked until the seven-category coded-condition mapping in Section 11.2 is defined.
                    </div>
                    <div className={styles.acts}>
                      <button className={styles.btnPrimary + " " + styles.btn} disabled={saving} onClick={() => requestT10Decision(exception, "exclusion_continue")}>Continue, with documented clinical rationale</button>
                      <button className={styles.btn} disabled={saving} onClick={() => requestT10Decision(exception, "exclusion_pause")}>Pause</button>
                      <button className={styles.btnDanger + " " + styles.btn} disabled={saving} onClick={() => requestT10Decision(exception, "exclusion_remove")}>Remove from alignment</button>
                    </div>
                    <div className={styles.acts}>
                      <button className={styles.btn} disabled={saving} onClick={() => requestPanelAction(exception, "add_note")}>Add a note to the record</button>
                      <button className={styles.btn} disabled={saving} onClick={() => requestPanelAction(exception, "message_beneficiary")}>Message the beneficiary</button>
                    </div>
                  </>
                )}
              </div>
            );
          }) : <div>No therapist action is waiting.</div>}

          <div className={styles.acts}>
            <button className={styles.btn} onClick={toggleFullHistory} disabled={historyLoading}>
              {historyLoading ? "Loading history..." : showFullHistory ? "Hide full history" : "Open the full history"}
            </button>
            <button className={styles.btn} onClick={() => onAuditExport(false)} disabled={auditExporting}>
              {auditExporting ? "Exporting..." : "Export audit file"}
            </button>
            <button className={styles.btn} onClick={() => onAuditExport(true)} disabled={auditExporting}>
              Export certified audit file
            </button>
          </div>
          {historyError && <div className={styles.note}>{historyError}</div>}
          {showFullHistory && <Timeline rows={fullHistory || []} emptyText="No history rows were returned." />}
        </div>

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Recent record activity</h5>
          <Timeline rows={timeline} emptyText="No recent record activity." />
        </div>

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Open implementation constraints from build specification Section 11</h5>
          <div className={styles.note}>
            11.1 Which exclusion-discovery outcome applies to which presentation remains an open clinical decision and is not inferred in code.<br />
            11.2 The mapping of the seven excluded categories to Unalignment API coded conditions remains undefined, so removal submission is blocked.<br />
            11.3 Pause duration and clinical criteria for resuming remain undefined.<br />
            11.4 An unattended exception is marked escalated at 48 hours, but where that escalation goes beyond this panel remains an operational decision.
          </div>
        </div>
      </div>
    </>
  );
}

function Row(props) {
  const { p, open, loadingDetail, detail, onToggle } = props;
  const primary = p.openExceptions?.[0] || null;
  const painLabel = p.pain_level != null ? `${p.pain_level} / 10` : "-";
  const waitLabel = primary
    ? `${primary.exceptionType} ${primary.kind === "decision" ? "Decision" : "Notification"}${primary.state === "escalated" ? " - Escalated" : ""}`
    : "None";

  return (
    <>
      <div
        className={cx(styles.brow, p.hasOpenReportException && styles.browExc, open && styles.browOpen)}
        onClick={() => onToggle(p.beneficiaryId)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onToggle(p.beneficiaryId);
        }}
      >
        <div className={styles.caret}>▶</div>
        <div className={styles.name}>
          {`${p.legalFirstName || ""} ${p.legalLastName || ""}`.trim() || `Beneficiary ${p.beneficiaryId}`}
          <span>ID {p.beneficiaryId}</span>
        </div>
        <div>{dash(p.state)}</div>
        <div className={styles.hideSm}><span className={styles.tagGrey + " " + styles.tag}>{dash(p.currentState)}</span></div>
        <div className={styles.hideSm}>{painLabel}</div>
        <div className={styles.hideSm}>{compactDate(p.next_reporting_window_open)}</div>
        <div>
          {primary ? (
            <span className={cx(styles.tag, (primary.kind === "decision" || primary.exceptionType === "T4b") ? styles.tagStop : styles.tagWarn)}>{waitLabel}</span>
          ) : <span className={cx(styles.tag, styles.tagGrey)}>None</span>}
        </div>
      </div>
      <div className={cx(styles.detail, open && styles.detailOn)}>
        {loadingDetail && !detail ? (
          <div>Loading beneficiary record...</div>
        ) : (
          <Detail {...props} d={detail || {}} />
        )}
      </div>
    </>
  );
}

export default function ReportPanel(props) {
  const [filter, setFilter] = useState("exc");
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [openRows, setOpenRows] = useState(new Set());
  const [summary, setSummary] = useState(null);
  const [people, setPeople] = useState([]);
  const [detailCache, setDetailCache] = useState({});
  const [loadingDetailRef, setLoadingDetailRef] = useState(null);
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [toast, setToast] = useState(null);
  const [painDecisionSavingRef, setPainDecisionSavingRef] = useState(null);
  const [exceptionDecisionSavingRef, setExceptionDecisionSavingRef] = useState(null);
  const [panelActionSavingRef, setPanelActionSavingRef] = useState(null);
  const [auditExportingRef, setAuditExportingRef] = useState(null);
  const [panelAuditExporting, setPanelAuditExporting] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleAuthError = useCallback((e) => {
    if (e?.response?.status === 401 || e?.response?.data?.responseCode === 401) {
      props.checkLoggedIn(false);
      return true;
    }
    return false;
  }, [props.checkLoggedIn]);

  const fetchSummary = useCallback(async () => {
    authHeader();
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/summary`);
      if (resp.data.success) setSummary(resp.data.data);
    } catch (e) {
      handleAuthError(e);
    }
  }, [handleAuthError]);

  const fetchBeneficiaries = useCallback(async () => {
    authHeader();
    setLoadingPeople(true);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiaries`, {
        params: { filter, state: stateFilter, search },
      });
      if (resp.data.success) setPeople(resp.data.data || []);
      else setPeople([]);
    } catch (e) {
      if (!handleAuthError(e)) setPeople([]);
    } finally {
      setLoadingPeople(false);
    }
  }, [filter, stateFilter, search, handleAuthError]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchBeneficiaries();
  }, [fetchBeneficiaries]);

  const fetchDetail = async (ref) => {
    authHeader();
    setLoadingDetailRef(ref);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}`);
      if (resp.data.success) {
        setDetailCache((prev) => ({ ...prev, [ref]: resp.data.data }));
        return resp.data.data;
      }
    } catch (e) {
      if (!handleAuthError(e)) setToast({ type: "error", text: e.response?.data?.message || "Failed to load the beneficiary record." });
    } finally {
      setLoadingDetailRef((cur) => (cur === ref ? null : cur));
    }
    return null;
  };

  const toggleRow = async (ref) => {
    const isOpening = !openRows.has(ref);
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.has(ref) ? next.delete(ref) : next.add(ref);
      return next;
    });
    if (isOpening && !detailCache[ref]) await fetchDetail(ref);
  };

  const refreshBeneficiary = async (ref) => {
    setDetailCache((prev) => {
      const next = { ...prev };
      delete next[ref];
      return next;
    });
    await Promise.all([fetchSummary(), fetchBeneficiaries(), fetchDetail(ref)]);
  };

  const submitPainDecision = async (p, exception, decision, extra = {}) => {
    const ref = p.beneficiaryId;
    const detail = detailCache[ref] || {};
    const source = exception.sourceSnapshot || {};
    const escalationId = source.painEscalationId || detail.painEscalation?.id || exception.sourceId;
    if (!escalationId) {
      setToast({ type: "error", text: "No open pain escalation record was found for this decision." });
      return false;
    }
    authHeader();
    setPainDecisionSavingRef(ref);
    try {
      const resp = await axios.post(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}/pain-escalation/decision`, {
        exceptionId: exception.exceptionId,
        escalationId,
        decision,
        revisitDate: extra.revisitDate || null,
        clinicalNeedsIdentified: extra.clinicalNeedsIdentified || null,
        clinicalDataRequested: extra.clinicalDataRequested ?? null,
        beneficiaryWording: extra.beneficiaryWording || null,
      });
      if (!resp.data.success) throw new Error(resp.data.message || "Failed to record the therapist decision.");
      setToast({ type: "success", text: "Therapist decision recorded." });
      await refreshBeneficiary(ref);
      return true;
    } catch (err) {
      if (!handleAuthError(err)) setToast({ type: "error", text: err.response?.data?.message || err.message || "Failed to record the therapist decision." });
      return false;
    } finally {
      setPainDecisionSavingRef((cur) => (cur === ref ? null : cur));
    }
  };

  const submitExceptionDecision = async (p, exception, decision, extra = {}) => {
    const ref = p.beneficiaryId;
    authHeader();
    setExceptionDecisionSavingRef(ref);
    try {
      const resp = await axios.post(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}/exception/decision`, {
        exceptionId: exception.exceptionId,
        decision,
        revisitDate: extra.revisitDate || null,
        clinicalRationale: extra.clinicalRationale || extra.clinicalNeedsIdentified || null,
        clinicalDataRequested: extra.clinicalDataRequested ?? null,
        beneficiaryWording: extra.beneficiaryWording || null,
      });
      if (!resp.data.success) throw new Error(resp.data.message || "Failed to record the therapist decision.");
      const blocker = resp.data.data?.submissionBlocked;
      setToast({ type: blocker ? "info" : "success", text: blocker || "Therapist decision recorded." });
      await refreshBeneficiary(ref);
      return true;
    } catch (err) {
      if (!handleAuthError(err)) setToast({ type: "error", text: err.response?.data?.message || err.message || "Failed to record the therapist decision." });
      return false;
    } finally {
      setExceptionDecisionSavingRef((cur) => (cur === ref ? null : cur));
    }
  };

  const submitPanelAction = async (p, action, reason, extra = {}) => {
    const ref = p.beneficiaryId;
    authHeader();
    setPanelActionSavingRef(ref);
    try {
      const resp = await axios.post(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}/action`, {
        action,
        reason,
        exceptionId: extra.exceptionId || null,
        reviewDate: extra.reviewDate || null,
        exclusionCategory: extra.exclusionCategory ?? null,
        clinicalDataRequested: extra.clinicalDataRequested ?? null,
        beneficiaryWording: extra.beneficiaryWording || null,
      });
      if (!resp.data.success) throw new Error(resp.data.message || "Failed to record the panel action.");
      const label = action === "message_beneficiary"
        ? "Message sent to the beneficiary."
        : action === "add_note"
          ? "Note added to the beneficiary record."
          : action === "record_exclusion_discovery"
            ? "Exclusion discovery recorded. A T10 decision is now on the panel."
            : "Notification acknowledged and closed.";
      setToast({ type: "success", text: label });
      await refreshBeneficiary(ref);
      return true;
    } catch (err) {
      if (!handleAuthError(err)) setToast({ type: "error", text: err.response?.data?.message || err.message || "Failed to record the panel action." });
      return false;
    } finally {
      setPanelActionSavingRef((cur) => (cur === ref ? null : cur));
    }
  };

  const saveAuditDownload = (resp, fallbackName) => {
    const disposition = resp.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = match?.[1] || fallbackName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportBeneficiaryAudit = async (p, certify = false) => {
    const ref = p.beneficiaryId;
    authHeader();
    setAuditExportingRef(ref);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}/audit-export`, {
        params: certify ? { certify: 1 } : {},
        responseType: "blob",
      });
      saveAuditDownload(resp, `access-audit-beneficiary-${ref}.html`);
      setToast({ type: "success", text: certify ? "Certified CMS ACCESS (Medicare) audit file exported." : "Beneficiary audit file exported." });
    } catch (err) {
      if (!handleAuthError(err)) {
        const text = err.response?.status === 403 && certify
          ? "Your account is not configured as an authorized CMS audit certifier."
          : "Failed to export the beneficiary audit file.";
        setToast({ type: "error", text });
      }
    } finally {
      setAuditExportingRef((cur) => (cur === ref ? null : cur));
    }
  };

  const exportPanelAudit = async (certify = false) => {
    const from = window.prompt("Audit start date (YYYY-MM-DD), or leave blank for all available history:", "");
    if (from === null) return;
    const to = window.prompt("Audit end date (YYYY-MM-DD), or leave blank through the present:", "");
    if (to === null) return;
    authHeader();
    setPanelAuditExporting(true);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/audit-export`, {
        params: { ...(from.trim() ? { from: from.trim() } : {}), ...(to.trim() ? { to: to.trim() } : {}), ...(certify ? { certify: 1 } : {}) },
        responseType: "blob",
      });
      saveAuditDownload(resp, `access-audit-panel-${new Date().toISOString().slice(0, 10)}.html`);
      setToast({ type: "success", text: certify ? "Certified CMS ACCESS (Medicare) panel audit register exported." : "Panel audit register exported." });
    } catch (err) {
      if (!handleAuthError(err)) {
        const text = err.response?.status === 403 && certify
          ? "Your account is not configured as an authorized CMS audit certifier."
          : "Failed to export the panel audit register. Check the date format and try again.";
        setToast({ type: "error", text });
      }
    } finally {
      setPanelAuditExporting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.bar}>
        <div className={styles.barIn}>
          <div className={styles.logo}><span className={styles.v}>Virdio</span><span className={styles.h}> Health</span></div>
          <div className={styles.who}>{summary?.therapistName ? `Supervising PT: ${summary.therapistName}` : "Supervising PT report"}</div>
        </div>
      </div>

      <main className={styles.wrap}>
        <h1>CMS ACCESS (Medicare) supervising PT exception panel</h1>
        <p className={styles.sub}>
          Routine care clears on the system record. This panel contains only therapist notifications and decisions, with persisted lifecycle clocks and clinical records.
        </p>

        {toast && <div className={toast.type === "success" ? styles.good : styles.note}>{toast.text}</div>}

        <div className={styles.strip}>
          <div className={styles.stat}><div className={styles.n}>{summary?.onPanel ?? "-"}</div><div className={styles.l}>On panel</div></div>
          <div className={cx(styles.stat, styles.statAlert)}><div className={styles.n}>{summary?.openDecisions ?? "-"}</div><div className={styles.l}>Open decisions</div></div>
          <div className={cx(styles.stat, styles.statWarn)}><div className={styles.n}>{summary?.openNotifications ?? "-"}</div><div className={styles.l}>Open notifications</div></div>
          <div className={styles.stat}><div className={styles.n}>{summary?.measureDueIn14Days ?? "-"}</div><div className={styles.l}>Measure due in 14 days</div></div>
          <div className={styles.stat}><div className={styles.n}>{summary?.careFurnishedThisMonth ?? "-"}</div><div className={styles.l}>Care furnished this month</div></div>
          <div className={styles.stat}><div className={styles.n}>{summary?.paused ?? "-"}</div><div className={styles.l}>Paused</div></div>
        </div>

        {!!summary?.licenses?.length && (
          <div className={styles.good}>
            <b>Licence coverage and expiry warning:</b>{" "}
            {summary.licenses.map((licence, i) => (
              <span key={`${licence.state}-${i}`}>{i ? "; " : ""}{licence.state}: expires {compactDate(licence.expiryDate)}{licence.expired ? " (expired)" : ""}</span>
            ))}
          </div>
        )}

        {!!summary?.routingAlerts?.length && (
          <div className={cx(styles.excbar, styles.excbarStop)}>
            <b>Routing alert: beneficiaries with no matching active therapist licence</b>
            {summary.routingAlerts.map((row) => (
              <div key={row.beneficiaryId}>{row.beneficiaryName || `Beneficiary ${row.beneficiaryId}`} - {row.state} - ID {row.beneficiaryId}</div>
            ))}
          </div>
        )}

        <div className={styles.filters}>
          {FILTERS.map((item) => (
            <button key={item.key} className={cx(styles.chip, filter === item.key && styles.chipOn)} onClick={() => setFilter(item.key)}>{item.label}</button>
          ))}
          <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
            {STATES.map((item) => <option value={item.value} key={item.value || "all"}>{item.label}</option>)}
          </select>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or beneficiary ID" />
          <button className={styles.btn} onClick={() => exportPanelAudit(false)} disabled={panelAuditExporting}>{panelAuditExporting ? "Exporting..." : "Export audit register"}</button>
          <button className={styles.btn} onClick={() => exportPanelAudit(true)} disabled={panelAuditExporting}>Export certified register</button>
        </div>

        <div className={styles.tbl}>
          <div className={styles.hrow}>
            <div></div>
            <div>Beneficiary</div>
            <div>State</div>
            <div className={styles.hideSm}>Care state</div>
            <div className={styles.hideSm}>Pain</div>
            <div className={styles.hideSm}>Next measure</div>
            <div>Waiting on therapist</div>
          </div>
          {loadingPeople ? (
            <div style={{ padding: "1.2rem" }}>Loading panel...</div>
          ) : people.length ? (
            people.map((p) => (
              <Row
                key={p.beneficiaryId}
                p={p}
                open={openRows.has(p.beneficiaryId)}
                loadingDetail={loadingDetailRef === p.beneficiaryId}
                detail={detailCache[p.beneficiaryId]}
                onToggle={toggleRow}
                onPainDecision={(exception, decision, extra) => submitPainDecision(p, exception, decision, extra)}
                painDecisionSaving={painDecisionSavingRef === p.beneficiaryId}
                onExceptionDecision={(exception, decision, extra) => submitExceptionDecision(p, exception, decision, extra)}
                exceptionDecisionSaving={exceptionDecisionSavingRef === p.beneficiaryId}
                onPanelAction={(action, reason, extra) => submitPanelAction(p, action, reason, extra)}
                panelActionSaving={panelActionSavingRef === p.beneficiaryId}
                onAuditExport={(certify) => exportBeneficiaryAudit(p, certify)}
                auditExporting={auditExportingRef === p.beneficiaryId}
              />
            ))
          ) : (
            <div style={{ padding: "1.2rem" }}>No beneficiaries match this filter.</div>
          )}
        </div>
      </main>
    </div>
  );
}
